import { useState, useEffect, Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import EventDetailsTab from './EventDetailsTab';
import EventAssigneesTab from './EventAssigneesTab';
import EventRemindersTab from './EventRemindersTab';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';

/**
 * Helper to get category from event type
 */
const getCategoryFromEventType = (eventType) => {
  switch (eventType) {
    case 'appointment':
      return 'external';
    case 'meeting':
    case 'task':
      return 'internal';
    case 'reminder':
    case 'time_block':
      return 'personal';
    case 'shift':
      return 'schedule';
    default:
      return 'external';
  }
};

/**
 * Helper to create initial form data from event or defaults
 */
const createInitialFormData = (event, defaultEventType) => {
  if (event) {
    const startDate = event.startTime ? parseISO(event.startTime) : new Date();
    const endDate = event.endTime ? parseISO(event.endTime) : new Date();
    return {
      title: event.title || '',
      description: event.description || '',
      eventType: event.eventType || 'appointment',
      category: event.category || 'external',
      startDate: format(startDate, 'yyyy-MM-dd'),
      startTime: event.allDay ? '00:00' : format(startDate, 'HH:mm'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      endTime: event.allDay ? '23:59' : format(endDate, 'HH:mm'),
      allDay: event.allDay || false,
      locationType: event.locationType || '',
      locationAddress: event.locationAddress || '',
      locationUrl: event.locationUrl || '',
      status: event.status || 'scheduled',
      color: event.color || '',
      visibility: event.visibility || 'private'
    };
  }
  // New event defaults
  const eventType = defaultEventType || 'appointment';
  return {
    title: '',
    description: '',
    eventType: eventType,
    category: getCategoryFromEventType(eventType),
    startDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endDate: format(new Date(), 'yyyy-MM-dd'),
    endTime: '10:00',
    allDay: false,
    locationType: '',
    locationAddress: '',
    locationUrl: '',
    status: 'scheduled',
    color: '',
    visibility: 'private'
  };
};

/**
 * EventSlideout - Full event editor slideout panel
 *
 * Design following EmployeeSlideout.js pattern:
 * - Smooth asymmetric animations
 * - Professional backdrop with blur
 * - Tab navigation (Details, Assignees, Reminders)
 * - Unsaved changes protection
 * - Form state lifted to parent to persist across tab switches
 */
function EventSlideout({
  isOpen,
  onClose,
  event,
  defaultEventType,
  onSave,
  onDelete,
  onStatusChange,
  shiftMode = false // When true, locks to shift event type and hides type/category selectors
}) {
  const [activeTab, setActiveTab] = useState('details');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Store event ID when delete dialog opens - prevents losing it if parent state changes
  const [pendingDeleteEventId, setPendingDeleteEventId] = useState(null);

  // Refs for Dialog initialFocus - fixes HeadlessUI FocusTrap error with nested dialogs
  const cancelCloseRef = useRef(null);
  const cancelDeleteRef = useRef(null);

  const isNewEvent = !event?.id;

  // Check if current user can delete this event
  // Permission rules:
  // - Creator can always delete (they made it)
  // - Organizer can delete (for meetings, the meeting organizer)
  // - For appointments/tasks/reminders: any assignee can delete (delegated work)
  // Backend enforces actual permission; frontend just controls button visibility
  const canDeleteEvent = () => {
    if (!event?.id) return false;

    // Creator can always delete their own events
    if (event.currentUserIsCreator) return true;

    // Organizer can delete (applies to meetings primarily)
    if (event.currentUserIsOrganizer) return true;

    // For delegated work items, any assignee can delete
    const eventType = event.eventType || currentEventType;
    if (['appointment', 'task', 'reminder'].includes(eventType)) {
      // If user is assigned, they can delete
      // Check currentUserIsAssignee flag (set by backend) or legacy response fields
      return event.currentUserIsAssignee ||
             event.currentUserRequiresResponse !== undefined ||
             event.currentUserResponseStatus !== undefined;
    }

    // For meetings and time_blocks without being creator/organizer, no delete
    return false;
  };

  // Track current event type for dynamic tabs (from event or form state)
  const [currentEventType, setCurrentEventType] = useState(event?.eventType || defaultEventType || 'appointment');

  // LIFTED STATE: Form data persists across tab switches
  // This state is lifted from EventDetailsTab to prevent loss when switching tabs
  const [formData, setFormData] = useState(() => createInitialFormData(event, defaultEventType));

  // LIFTED STATE: Selected customer ID for appointments/tasks/reminders
  const [selectedCustomerId, setSelectedCustomerId] = useState(event?.customerId || '');

  // LIFTED STATE: Invitees for meetings (new events only - existing events use API)
  const [invitees, setInvitees] = useState([]);

  // Determine which tabs to show based on event type
  // - Meetings: Details only (Invitees inline), no Assignees tab
  // - Time Block: Details only, no Assignees tab
  // - Shifts: Details + Reminders (Employee inline in Details)
  // - Appointment/Task/Reminder: Details + Assignees + Reminders
  const getVisibleTabs = (eventType) => {
    const baseTabs = [
      { key: 'details', label: shiftMode ? 'Shift Details' : 'Event Details' },
      { key: 'reminders', label: 'Reminders' }
    ];

    // Meetings have invitees inline in Details, no separate Assignees tab
    // Time blocks don't need assignees
    // Shifts have employee assignment inline in Details
    if (eventType === 'meeting' || eventType === 'time_block' || eventType === 'shift') {
      return baseTabs;
    }

    // Appointments, Tasks, Reminders get the Assignees tab
    return [
      { key: 'details', label: 'Event Details' },
      { key: 'assignees', label: 'Assignees' },
      { key: 'reminders', label: 'Reminders' }
    ];
  };

  // Reset state when slideout opens
  // Note: We intentionally use event?.id as the dependency for "new event" detection
  // rather than the full event object to prevent unnecessary re-renders
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
      setHasUnsavedChanges(false);
      setShowConfirmClose(false);
      setShowConfirmDelete(false);
      setIsDeleting(false);
      setPendingDeleteEventId(null);
      setCurrentEventType(event?.eventType || defaultEventType || 'appointment');
      // Reset lifted form state
      setFormData(createInitialFormData(event, defaultEventType));
      setSelectedCustomerId(event?.customerId || '');
      // Prefill assignees for new shifts opened from schedule grid (draft event, no id)
      setInvitees(event?.assignees || []);
    }
    // Include startTime so cell-create drafts with different days re-init form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, event?.id, event?.eventType, event?.startTime, defaultEventType]);

  // Handle close with unsaved changes check
  const handleClose = () => {
    // Don't close slideout if delete confirmation is open
    if (showConfirmDelete) {
      return;
    }
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  // Force close without saving
  const handleForceClose = () => {
    setShowConfirmClose(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Open delete confirmation - stores event ID to prevent losing it
  const openDeleteConfirmation = () => {
    if (event?.id) {
      setPendingDeleteEventId(event.id);
      setShowConfirmDelete(true);
    }
  };

  // Handle delete - uses stored pendingDeleteEventId
  const handleDelete = async () => {
    const eventIdToDelete = pendingDeleteEventId;

    if (!eventIdToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDelete(eventIdToDelete);
      // Close modal on success - onDelete callback will close the slideout
      setShowConfirmDelete(false);
      setPendingDeleteEventId(null);
      setIsDeleting(false);
    } catch (error) {
      console.error('Failed to delete event:', error);
      setIsDeleting(false);
    }
  };

  // Get category badge styling
  const getCategoryBadge = (category) => {
    const variants = {
      external: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', label: 'External' },
      internal: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500', label: 'Internal' },
      personal: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', label: 'Personal' },
      schedule: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Schedule' }
    };
    return variants[category] || variants.external;
  };

  // Get dynamic tabs based on current event type
  const tabs = getVisibleTabs(currentEventType);

  // Handle event type change from details tab (for new events or edits)
  const handleEventTypeChange = (newEventType) => {
    setCurrentEventType(newEventType);
    // If current tab is not visible in new tab set, reset to details
    const newTabs = getVisibleTabs(newEventType);
    if (!newTabs.find(t => t.key === activeTab)) {
      setActiveTab('details');
    }
  };

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-400"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-10">
                {/* Slideout Panel */}
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-out duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col bg-white shadow-2xl">

                      {/* Header */}
                      <div className="bg-white px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-200">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                            {shiftMode
                              ? (isNewEvent ? 'New Shift' : 'Edit Shift')
                              : (isNewEvent ? 'New Event' : 'Edit Event')}
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center gap-2">
                            {/* Delete button (only for existing events and if user has permission) */}
                            {!isNewEvent && canDeleteEvent() && (
                              <PermissionGate permission={PERMISSIONS.scheduling.delete}>
                                <button
                                  type="button"
                                  onClick={openDeleteConfirmation}
                                  className="rounded-md bg-white text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                                  title="Delete event"
                                  tabIndex={-1}
                                >
                                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                              </PermissionGate>
                            )}
                            {/* Close button */}
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                              onClick={handleClose}
                              tabIndex={-1}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>

                        {/* Event Summary (for existing events) */}
                        {!isNewEvent && event && (
                          <div className="mt-6">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-gray-900 truncate">
                                  {event.title}
                                </h3>
                                {event.description && (
                                  <p className="text-sm text-gray-500 truncate mt-0.5">
                                    {event.description}
                                  </p>
                                )}
                              </div>
                              {/* Category Badge */}
                              {event.category && (() => {
                                const badge = getCategoryBadge(event.category);
                                return (
                                  <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Tabs */}
                      <div className="border-b border-gray-200 bg-gray-50 px-4 sm:px-6 overflow-x-auto">
                        <nav className="-mb-px flex gap-4 sm:gap-8" aria-label="Tabs">
                          {tabs.map((tab) => (
                            <button
                              key={tab.key}
                              onClick={() => setActiveTab(tab.key)}
                              className={`
                                relative whitespace-nowrap py-3 sm:py-4 px-1 font-medium text-xs sm:text-sm transition-colors duration-200
                                ${activeTab === tab.key
                                  ? 'text-blue-600'
                                  : 'text-gray-500 hover:text-gray-700'
                                }
                              `}
                            >
                              <span className="hidden sm:inline">{tab.label}</span>
                              <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                              {activeTab === tab.key && (
                                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                              )}
                            </button>
                          ))}
                        </nav>
                      </div>

                      {/* Tab Content */}
                      <div className="flex-1 overflow-y-auto">
                        {activeTab === 'details' && (
                          <EventDetailsTab
                            event={event}
                            defaultEventType={defaultEventType}
                            onSave={onSave}
                            onClose={onClose}
                            onUnsavedChanges={setHasUnsavedChanges}
                            onEventTypeChange={handleEventTypeChange}
                            onStatusChange={onStatusChange}
                            // Lifted state props - persists across tab switches
                            formData={formData}
                            setFormData={setFormData}
                            selectedCustomerId={selectedCustomerId}
                            setSelectedCustomerId={setSelectedCustomerId}
                            invitees={invitees}
                            setInvitees={setInvitees}
                            // Shift mode - hides event type/category selectors
                            shiftMode={shiftMode}
                          />
                        )}
                        {activeTab === 'assignees' && (
                          <EventAssigneesTab
                            event={event}
                            onUnsavedChanges={setHasUnsavedChanges}
                          />
                        )}
                        {activeTab === 'reminders' && (
                          <EventRemindersTab
                            event={event}
                            onUnsavedChanges={setHasUnsavedChanges}
                          />
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Close Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelCloseRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmClose(false)}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2"
                      onClick={handleForceClose}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Delete Dialog */}
      <Transition.Root show={showConfirmDelete} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmDelete(false)} initialFocus={cancelDeleteRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Delete Event
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete this event? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelDeleteRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmDelete(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2 disabled:opacity-50"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Deleting...' : 'Delete Event'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

export default EventSlideout;
